import { zodResolver } from '@hookform/resolvers/zod'
import { Loader2, Wrench } from 'lucide-react'
import { Controller, useForm, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { z } from 'zod'
import { Button } from '@/components/ui/button'
import { DateField } from '@/components/ui/date-field'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Modal } from '@/components/ui/modal'
import { FormSelect } from '@/components/ui/select'
import { INSTALLATION_KINDS, INSTALLATION_STATUSES, type Installation, type InstallationKind, type Project } from '../../types'
import { useCustomers } from '@/features/crm/api'
import { toastError, useStaffLookup } from '../../ui'
import { useSaveInstallation } from '../../workApi'

type Props = {
  open: boolean
  project: Project
  installation: Installation | null
  defaultKind?: InstallationKind
  onOpenChange: (v: boolean) => void
  onSaved?: (id: string) => void
}

export function InstallationFormModal({ open, onOpenChange, ...rest }: Props) {
  return open ? <InstallationForm key={rest.installation?.id ?? 'new'} {...rest} onDone={() => onOpenChange(false)} /> : null
}

function InstallationForm({ project, installation, defaultKind = 'installation', onDone, onSaved }: Omit<Props, 'open' | 'onOpenChange'> & { onDone: () => void }) {
  const { t } = useTranslation()
  const save = useSaveInstallation(project.id)
  const { nameOf } = useStaffLookup()
  // Kurulum CRM müşteri hesabına bağlıdır; şehir hesap kartından gelir.
  const customers = useCustomers()

  const schema = z
    .object({
      accountId: z.string().min(1, t('projects.install.form.customerRequired')),
      kind: z.enum(INSTALLATION_KINDS),
      ownerId: z.string(),
      status: z.enum(INSTALLATION_STATUSES),
      startDate: z.string().min(1, t('projects.form.dateRequired')),
      targetDate: z.string().min(1, t('projects.form.dateRequired')),
    })
    .refine((v) => !v.startDate || !v.targetDate || v.targetDate >= v.startDate, { path: ['targetDate'], message: t('projects.form.endBeforeStart') })

  const form = useForm({
    resolver: zodResolver(schema),
    defaultValues: {
      accountId: installation?.accountId ?? '',
      kind: installation?.kind ?? defaultKind,
      ownerId: installation?.ownerId ?? '',
      status: installation?.status ?? 'planned',
      startDate: installation?.startDate ?? '',
      targetDate: installation?.targetDate ?? '',
    },
  })
  const { errors } = form.formState
  const startDate = useWatch({ control: form.control, name: 'startDate' })
  const kind = useWatch({ control: form.control, name: 'kind' })
  const accountId = useWatch({ control: form.control, name: 'accountId' })
  const city = installation && installation.accountId === accountId ? installation.city : (customers.data?.find((c) => c.id === accountId)?.city ?? '')

  const onSubmit = form.handleSubmit(async (v) => {
    try {
      const saved = await save.mutateAsync({ id: installation?.id, input: { ...v, ownerId: v.ownerId || null } })
      toast.success(t(installation ? 'projects.install.updated' : 'projects.install.created'))
      onSaved?.(saved.id)
      onDone()
    } catch (e) {
      toastError(e)
    }
  })

  const err = (k: keyof typeof errors) => (errors[k] ? <p className="text-xs text-destructive">{errors[k]?.message}</p> : null)

  return (
    <Modal
      open
      size="lg"
      onOpenChange={(v) => !v && onDone()}
      dismissible={!save.isPending}
      icon={<Wrench />}
      title={t(installation ? 'projects.install.form.editTitle' : kind === 'demo' ? 'projects.install.form.createDemo' : 'projects.install.form.createInstall')}
      description={t('projects.install.form.hint', { project: project.name })}
      onSubmit={(e) => void onSubmit(e)}
      footer={
        <>
          <Button type="button" variant="cancel" onClick={onDone}>
            {t('common.cancel')}
          </Button>
          <Button type="submit" disabled={save.isPending}>
            {save.isPending ? <Loader2 className="animate-spin" /> : null}
            {t('common.save')}
          </Button>
        </>
      }
    >
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div className="space-y-2">
          <Label htmlFor="in-customer">{t('projects.install.columns.customer')}</Label>
          <FormSelect
            id="in-customer"
            control={form.control}
            name="accountId"
            disabled={!!installation}
            options={
              installation && !customers.data?.some((c) => c.id === installation.accountId)
                ? [{ value: installation.accountId, label: installation.customer }]
                : (customers.data ?? []).map((c) => ({ value: c.id, label: c.name, description: [c.district, c.city].filter(Boolean).join(', ') }))
            }
          />
          {err('accountId')}
        </div>
        <div className="space-y-2">
          <Label htmlFor="in-city">{t('projects.install.form.city')}</Label>
          <Input id="in-city" value={city} readOnly />
        </div>
        <div className="space-y-2">
          <Label htmlFor="in-kind">{t('projects.install.columns.kind')}</Label>
          <FormSelect id="in-kind" control={form.control} name="kind" options={INSTALLATION_KINDS.map((k) => ({ value: k, label: t(`projects.installKind.${k}`) }))} />
        </div>
        <div className="space-y-2">
          <Label htmlFor="in-status">{t('projects.install.columns.status')}</Label>
          <FormSelect id="in-status" control={form.control} name="status" options={INSTALLATION_STATUSES.map((s) => ({ value: s, label: t(`projects.installStatus.${s}`) }))} />
        </div>
        <div className="space-y-2 sm:col-span-2">
          <Label htmlFor="in-owner">{t('projects.install.columns.owner')}</Label>
          <FormSelect
            id="in-owner"
            control={form.control}
            name="ownerId"
            options={[{ value: '', label: t('projects.work.unassigned') }, ...project.team.map((m) => ({ value: m.userId, label: nameOf(m.userId), description: m.title }))]}
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="in-start">{t('projects.fields.startDate')}</Label>
          <Controller control={form.control} name="startDate" render={({ field }) => <DateField id="in-start" value={field.value} onChange={field.onChange} aria-invalid={!!errors.startDate} />} />
          {err('startDate')}
        </div>
        <div className="space-y-2">
          <Label htmlFor="in-target">{t('projects.install.columns.target')}</Label>
          <Controller
            control={form.control}
            name="targetDate"
            render={({ field }) => <DateField id="in-target" value={field.value} onChange={field.onChange} min={startDate || undefined} aria-invalid={!!errors.targetDate} />}
          />
          {err('targetDate')}
        </div>
      </div>
    </Modal>
  )
}
